/**
 * 이사유형 instance 의 답변(answers)을 사람이 읽기 좋은 한국어 한 줄/여러 줄
 * 설명으로 합성한다.
 *
 * "선택한 이사 유형" review 시트에서 chip 대신 표시되는 텍스트를 만든다.
 * `InquiryFlow.resolveInstanceSummary` prop 으로 함수 자체를 주입한다.
 *
 * 두 단계 합성:
 *   1. 이사유형 코드별 specialization (도메인 자연어, 예: "사이청소 / 13시 퇴거 16시 입주")
 *   2. fallback: spec.attributes 순회하며 generic "label: value" 합성
 *
 * 직렬화 규칙:
 *   - TIME    "13:00" → "13시", "13:30" → "13시 30분"
 *   - DATE    "2026-04-15" → "4월 15일"
 *   - DATE_RANGE JSON {startDate, endDate} → "4월 15일 ~ 4월 20일"
 *   - CHOICE_WITH_CUSTOM {choice, text} → text 우선, 없으면 choice 의 label
 *   - ENUM     value → option.label
 *   - BOOLEAN  true/false → "예"/"아니오"
 *   - NUMBER/INT → 숫자 그대로
 *   - STRING / address → 그대로 / roadAddress
 */

import { isExtensibleCustomValue, type ItemPickerInstance } from '@miso/design-system';
import type {
  MovingTypeSpecificationAttribute,
  MovingTypeSpecificationResponse,
} from '@/entities/visit-counsel/model/moving-type-intake.types';
import {
  resolveMovingTypeAttribute,
  type ResolvedMovingTypeAttributeType,
} from '@/features/visit-counsel/intake-form/model/resolveMovingTypeAttributeKind';
import {
  attrToQuestionId,
  DATE_RANGE_START_SUFFIX,
  DATE_RANGE_END_SUFFIX,
} from './movingTypeSpecToFlowDocument';

// ── 이사유형 코드 ──────────────────────────────────────────────────────────────

const CODE_DELAYED = 'DELAYED';
const CODE_STORAGE = 'STORAGE';
const CODE_STOPOVER = 'STOPOVER';

// ── value 포맷터(타입별) ──────────────────────────────────────────────────────

function formatTime(value: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return value;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return value;
  if (min === 0) return `${h}시`;
  return `${h}시 ${min}분`;
}

function formatDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (Number.isNaN(month) || Number.isNaN(day)) return value;
  return `${month}월 ${day}일`;
}

function formatDateRange(jsonOrObj: unknown): string | null {
  let parsed: Record<string, unknown> | null = null;
  if (typeof jsonOrObj === 'string') {
    if (!jsonOrObj.startsWith('{')) return null;
    try {
      parsed = JSON.parse(jsonOrObj) as Record<string, unknown>;
    } catch {
      return null;
    }
  } else if (typeof jsonOrObj === 'object' && jsonOrObj !== null) {
    parsed = jsonOrObj as Record<string, unknown>;
  }
  if (!parsed) return null;
  const start = typeof parsed.startDate === 'string' ? parsed.startDate : null;
  const end = typeof parsed.endDate === 'string' ? parsed.endDate : null;
  if (start && end) return `${formatDate(start)} ~ ${formatDate(end)}`;
  if (start) return `${formatDate(start)}부터`;
  if (end) return `${formatDate(end)}까지`;
  return null;
}

function formatBoolean(value: unknown): string | null {
  if (value === 'true' || value === true) return '예';
  if (value === 'false' || value === false) return '아니오';
  return null;
}

function formatChoiceWithCustom(
  value: unknown,
  resolved: ResolvedMovingTypeAttributeType
): string | null {
  // extensible 답변 객체 (sub-flow 내부 진행 중에 유지된 raw 값)
  if (isExtensibleCustomValue(value)) {
    return value.text?.trim() || resolveEnumLabel(value.choice, resolved);
  }
  // 직렬화된 string — JSON 객체이거나 단순 옵션 value
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const obj = JSON.parse(trimmed) as { choice?: unknown; text?: unknown };
        const text = typeof obj.text === 'string' ? obj.text.trim() : '';
        if (text) return text;
        if (typeof obj.choice === 'string') return resolveEnumLabel(obj.choice, resolved);
      } catch {
        // fall through
      }
    }
    return resolveEnumLabel(trimmed, resolved);
  }
  return null;
}

function resolveEnumLabel(value: string, resolved: ResolvedMovingTypeAttributeType): string {
  return resolved.enumDisplayNames?.[value] ?? value;
}

function formatAddress(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const obj = JSON.parse(trimmed) as { roadAddress?: unknown; buildingName?: unknown };
        return formatAddressObject(obj);
      } catch {
        return trimmed || null;
      }
    }
    return trimmed || null;
  }
  if (typeof value === 'object' && value !== null) {
    return formatAddressObject(value as Record<string, unknown>);
  }
  return null;
}

function formatAddressObject(obj: Record<string, unknown>): string | null {
  const road = typeof obj.roadAddress === 'string' ? obj.roadAddress.trim() : '';
  const bldg = typeof obj.buildingName === 'string' ? obj.buildingName.trim() : '';
  if (road && bldg) return `${road} (${bldg})`;
  return road || bldg || null;
}

// ── 단일 attribute → 사람 친화적 값 문자열 ────────────────────────────────────

/**
 * answers 를 보고 attribute 의 값을 사람 친화적 문자열로 만든다.
 * DATE_RANGE 는 sub-key 두 개 모두 참조(직렬화 전 단계 답변에서 가능).
 * 직렬화된 형태도 동일하게 처리.
 */
export function formatAttributeValue(
  attr: MovingTypeSpecificationAttribute,
  answers: Record<string, unknown>,
  resolved: ResolvedMovingTypeAttributeType = resolveMovingTypeAttribute(attr)
): string | null {
  const baseId = attrToQuestionId(attr.key);

  switch (resolved.kind) {
    case 'TIME': {
      const v = answers[baseId];
      return typeof v === 'string' && v ? formatTime(v) : null;
    }
    case 'DATE': {
      const v = answers[baseId];
      return typeof v === 'string' && v ? formatDate(v) : null;
    }
    case 'DATE_RANGE': {
      // sub-flow 진행 중: __start, __end 두 sub-key
      const startVal = answers[`${baseId}${DATE_RANGE_START_SUFFIX}`];
      const endVal = answers[`${baseId}${DATE_RANGE_END_SUFFIX}`];
      if (typeof startVal === 'string' || typeof endVal === 'string') {
        return formatDateRange({
          ...(typeof startVal === 'string' ? { startDate: startVal } : {}),
          ...(typeof endVal === 'string' ? { endDate: endVal } : {}),
        });
      }
      // 직렬화된 형태: 단일 baseId 에 JSON
      return formatDateRange(answers[baseId]);
    }
    case 'BOOLEAN': {
      return formatBoolean(answers[baseId]);
    }
    case 'CHOICE_WITH_CUSTOM': {
      return formatChoiceWithCustom(answers[baseId], resolved);
    }
    case 'ENUM': {
      const v = answers[baseId];
      if (typeof v !== 'string' || !v) return null;
      return resolveEnumLabel(v, resolved);
    }
    case 'STRUCTURED_DEPARTURE_ADDRESS': {
      return formatAddress(answers[baseId]);
    }
    case 'NUMBER':
    case 'INT': {
      const v = answers[baseId];
      if (typeof v === 'number') return String(v);
      if (typeof v === 'string' && v) return v;
      return null;
    }
    case 'STRING':
    default: {
      const v = answers[baseId];
      return typeof v === 'string' && v ? v : null;
    }
  }
}

// ── 도메인 specialization ─────────────────────────────────────────────────────

interface SpecializationContext {
  answers: Record<string, unknown>;
  spec: MovingTypeSpecificationResponse;
  attrByKey: Map<string, MovingTypeSpecificationAttribute>;
  resolvedByKey: Map<string, ResolvedMovingTypeAttributeType>;
}

function getValue(ctx: SpecializationContext, key: string): string | null {
  const attr = ctx.attrByKey.get(key);
  if (!attr) return null;
  return formatAttributeValue(attr, ctx.answers, ctx.resolvedByKey.get(key));
}

/**
 * Attribute label 에서 시간의 "동작"을 추론한다.
 *
 * 대기이사의 시간 attribute 는 spec 마다 key 명이 다양해(`startTime` /
 * `waitStartTime` / `departureTime` / `desiredCheckOutTime` ...) key 매칭은 깨지기
 * 쉽다. 대신 attribute 의 한국어 label(`name`)에 들어있는 keyword 로 동작 단어를
 * 결정한다. 매칭이 안 되면 label 자체를 그대로 쓴다(`"이사 시간": "13시 이사 시간"`
 * 처럼 어색하지 않게 보장하는 건 spec 작성자 책임).
 *
 * - 퇴거/체크아웃/move out/out → "퇴거"
 * - 입주/체크인/move in/in → "입주"
 * - 시작/start → "시작"
 * - 종료/end → "종료"
 * - 그 외 → label trim
 */
function inferTimeAction(label: string | null | undefined): string {
  const trimmed = (label ?? '').trim();
  if (!trimmed) return '';
  if (/(퇴거|체크\s*아웃|move\s*out|check\s*out)/i.test(trimmed)) return '퇴거';
  if (/(입주|체크\s*인|move\s*in|check\s*in)/i.test(trimmed)) return '입주';
  if (/(시작|start)/i.test(trimmed)) return '시작';
  if (/(종료|end)/i.test(trimmed)) return '종료';
  return trimmed;
}

/**
 * Attribute label / key 에서 "사유" 의미를 갖는지 추론한다.
 * - `/사유|이유|reason/i` → true.
 */
function looksLikeReason(attr: MovingTypeSpecificationAttribute): boolean {
  const haystack = `${attr.name ?? ''} ${attr.key ?? ''}`;
  return /(사유|이유|reason)/i.test(haystack);
}

/**
 * 대기이사 (DELAYED) — "차 한 잔의 여유를 가지고 싶어서 / 13시 퇴거 16시 입주"
 * 같은 도메인 자연어를 만든다.
 *
 * 전략:
 *   1. spec.attributes 를 traverse.
 *   2. CHOICE_WITH_CUSTOM 또는 STRING 중 label 이 "사유/이유/reason" 인 attribute 의
 *      값을 reason 텍스트로 사용 (없으면 후보 key fallback).
 *   3. type === TIME 인 모든 attribute 의 값을 spec 정의 순서대로 합성.
 *      label keyword 로 "퇴거"/"입주"/"시작"/"종료" 동작 단어 결정.
 *
 * key 명에만 의존하던 기존 로직이 spec 의 실제 attribute key 와 어긋나
 * 시간이 누락되던 문제를 해결.
 */
function summarizeDelayed(ctx: SpecializationContext): string | null {
  const attrs = ctx.spec.attributes ?? [];

  // 1) reason: label/key 가 "사유" 패턴인 첫 attribute. 못 찾으면 명시적 후보 키.
  const reasonAttr = attrs.find(looksLikeReason);
  const reason = reasonAttr
    ? formatAttributeValue(reasonAttr, ctx.answers, ctx.resolvedByKey.get(reasonAttr.key))
    : (getValue(ctx, 'reason') ?? getValue(ctx, 'waitReason') ?? getValue(ctx, 'delayReason'));

  // 2) 모든 TIME attribute 를 spec 정의 순서대로 모은다.
  const timeFragments: string[] = [];
  for (const attr of attrs) {
    const resolved = ctx.resolvedByKey.get(attr.key);
    if (resolved?.kind !== 'TIME') continue;
    const value = formatAttributeValue(attr, ctx.answers, resolved);
    if (!value) continue;
    const action = inferTimeAction(attr.name);
    timeFragments.push(action ? `${value} ${action}` : value);
  }

  // 3) TIME attribute 가 spec 에 정의돼 있지 않은 경우(=옛 backend) 기존 후보 키 fallback.
  if (timeFragments.length === 0) {
    const startTime =
      getValue(ctx, 'startTime') ??
      getValue(ctx, 'waitStartTime') ??
      getValue(ctx, 'departureTime') ??
      getValue(ctx, 'desiredCheckOutTime') ??
      getValue(ctx, 'desiredMoveOutTime') ??
      getValue(ctx, 'checkOutTime') ??
      getValue(ctx, 'moveOutTime') ??
      getValue(ctx, 'startAt');
    const endTime =
      getValue(ctx, 'endTime') ??
      getValue(ctx, 'waitEndTime') ??
      getValue(ctx, 'arrivalTime') ??
      getValue(ctx, 'desiredCheckInTime') ??
      getValue(ctx, 'desiredMoveInTime') ??
      getValue(ctx, 'checkInTime') ??
      getValue(ctx, 'moveInTime') ??
      getValue(ctx, 'endAt');
    if (startTime) timeFragments.push(`${startTime} 퇴거`);
    if (endTime) timeFragments.push(`${endTime} 입주`);
  }

  const timeFragment = timeFragments.length > 0 ? timeFragments.join(' ') : null;

  const parts = [reason, timeFragment].filter(Boolean) as string[];
  if (parts.length === 0) return null;
  return parts.join(' / ');
}

/**
 * 보관이사 (STORAGE) — "보관 4월 15일 ~ 4월 20일" 같은 자연어.
 */
function summarizeStorage(ctx: SpecializationContext): string | null {
  const period =
    getValue(ctx, 'period') ?? getValue(ctx, 'storagePeriod') ?? getValue(ctx, 'duration');
  if (period) return `보관 ${period}`;
  return null;
}

/**
 * 경유이사 (STOPOVER) — "경유: 서울시 강남구 (아무빌딩)" 같은 자연어.
 */
function summarizeStopover(ctx: SpecializationContext): string | null {
  const waypoint =
    getValue(ctx, 'waypoint') ?? getValue(ctx, 'stopoverAddress') ?? getValue(ctx, 'address');
  if (waypoint) return `경유 ${waypoint}`;
  return null;
}

const SPECIALIZATIONS: Record<string, (ctx: SpecializationContext) => string | null> = {
  [CODE_DELAYED]: summarizeDelayed,
  [CODE_STORAGE]: summarizeStorage,
  [CODE_STOPOVER]: summarizeStopover,
};

// ── Generic fallback ─────────────────────────────────────────────────────────

/**
 * spec.attributes 순회하며 "label: value, label: value" 형식으로 합성.
 * specialization 이 null 을 반환하거나 코드 매칭이 안 되는 경우 fallback.
 */
function summarizeGeneric(ctx: SpecializationContext): string | null {
  const parts: string[] = [];
  for (const attr of ctx.spec.attributes ?? []) {
    const value = formatAttributeValue(attr, ctx.answers, ctx.resolvedByKey.get(attr.key));
    if (!value) continue;
    const label = attr.name?.trim() || attr.key;
    parts.push(`${label}: ${value}`);
  }
  return parts.length > 0 ? parts.join(', ') : null;
}

// ── Public API ───────────────────────────────────────────────────────────────

export interface SummarizeArgs {
  instance: ItemPickerInstance;
  spec: MovingTypeSpecificationResponse;
}

/**
 * 단일 instance + 매칭되는 spec 으로 사람 친화적 요약문을 만든다.
 *
 * - SPECIALIZATIONS[spec.code] 가 truthy 한 결과를 내면 그것을 우선.
 * - 없거나 null 이면 generic "label: value, ..." 합성.
 * - 답변이 전혀 없으면 null (시트는 chip 도 없는 빈 row 로 표시).
 */
export function summarizeMovingTypeInstance({ instance, spec }: SummarizeArgs): string | null {
  const attrs = spec.attributes ?? [];
  const attrByKey = new Map(attrs.map((a) => [a.key, a]));
  const resolvedByKey = new Map(attrs.map((a) => [a.key, resolveMovingTypeAttribute(a)] as const));
  const ctx: SpecializationContext = {
    answers: instance.answers ?? {},
    spec,
    attrByKey,
    resolvedByKey,
  };

  const specialized = SPECIALIZATIONS[spec.code]?.(ctx);
  if (specialized) return specialized;

  return summarizeGeneric(ctx);
}

/**
 * 여러 spec 중 itemId 에 매칭되는 spec 을 찾아 요약하는 어댑터.
 * `InquiryFlow.resolveInstanceSummary` prop 에 그대로 전달 가능한 시그니처.
 */
export function makeMovingTypeInstanceSummarizer(
  specs: MovingTypeSpecificationResponse[] | null | undefined
): (instance: ItemPickerInstance) => string | null {
  const byCode = new Map((specs ?? []).map((s) => [s.code, s]));
  return (instance) => {
    const spec = byCode.get(instance.itemId);
    if (!spec) return null;
    return summarizeMovingTypeInstance({ instance, spec });
  };
}
